import type { WorkTypeCode } from "../api/types";

/** Work types (UX-01 3.2, GRF-10): a coloured pictogram tile (shapes in WorkTypeIcon.tsx) plus a label, never colour alone. */
export interface WorkTypeInfo {
  label: string;
  tone: string;
  /** RAID types: white tile with an ink border. */
  register?: boolean;
  /** Types that never appear on sprint or Kanban boards by default (S14-AC2). */
  offBoard?: boolean;
}

export const WORK_TYPES: Record<WorkTypeCode, WorkTypeInfo> = {
  EPIC: { label: "Epic", tone: "var(--type-epic)" },
  // Change Management (2026-09-19): a CR never shows as its own card or backlog row (offBoard, same as the RAID
  // types below) — its children show it as a parent chip instead. Unlike RAID, a CR is not a register: it keeps its
  // own tile colour (not `register`'s white/ink-border look) and stays a normal item in the project's Items list.
  CR: { label: "Change request", tone: "var(--type-cr)", offBoard: true },
  STORY: { label: "User story", tone: "var(--type-story)" },
  TASK: { label: "Task", tone: "var(--type-task)" },
  SUBTASK: { label: "Sub-task", tone: "var(--type-subtask)" },
  DEFECT: { label: "Defect", tone: "var(--type-defect)" },
  DEPLOYMENT: { label: "Deployment", tone: "var(--type-deploy)" },
  SERVICE_REQUEST: { label: "Service request", tone: "var(--type-request)" },
  INCIDENT: { label: "Incident", tone: "var(--type-incident)" },
  // M-Issue-Action-Split (2026-09-19): ISSUE_ACTION split into two RAID register types, same tile family as RISK.
  // Kept ISSUE_ACTION itself rather than deleting it — real fixtures/tests/seed data still reference it (see the
  // WorkTypeCode enum comment in contracts/openapi.yaml) — but it is no longer offered for new items
  // (CREATE_WORK_TYPE_ORDER below excludes it; ItemForm.tsx picks that list only while creating).
  ISSUE_ACTION: { label: "Issue or action", tone: "var(--type-raid)", register: true, offBoard: true },
  ISSUE: { label: "Issue", tone: "var(--type-raid)", register: true, offBoard: true },
  ACTION: { label: "Action", tone: "var(--type-raid)", register: true, offBoard: true },
  RISK: { label: "Risk", tone: "var(--type-raid)", register: true, offBoard: true },
};

export const WORK_TYPE_ORDER: readonly WorkTypeCode[] = [
  "STORY",
  "TASK",
  "SUBTASK",
  "DEFECT",
  "EPIC",
  "CR",
  "SERVICE_REQUEST",
  "INCIDENT",
  "DEPLOYMENT",
  "ISSUE_ACTION",
  "ISSUE",
  "ACTION",
  "RISK",
];

/**
 * Types offered when creating a new item (ItemForm.tsx, `mode === "create"` only): the same list minus
 * ISSUE_ACTION, which stays in {@link WORK_TYPE_ORDER} so an existing ISSUE_ACTION item still renders correctly
 * everywhere else (filters, badges, its own disabled type field when editing) — it just never appears as a choice
 * for a brand-new item now that ISSUE and ACTION exist.
 */
export const CREATE_WORK_TYPE_ORDER: readonly WorkTypeCode[] = WORK_TYPE_ORDER.filter((type) => type !== "ISSUE_ACTION");

export function workTypeLabel(type: string): string {
  return (WORK_TYPES as Record<string, WorkTypeInfo | undefined>)[type]?.label ?? type;
}
